import React, { useState, useMemo, useEffect, useRef } from "react";
import { Table, TableBody, TableCell, TableHeader, TableRow } from "../components/ui/table";
import Button from "../components/ui/button/Button";
import InputField from "../components/ui/input/InputField";
import { 
  Plus, Loader2, X, Save, Check, Edit2,Trash2,
  Video, Image as ImageIcon, Smartphone, Layers, FileText, PlayCircle 
} from "lucide-react";
import postTypeService from "../api/postTypeService";
import toast, { Toaster } from "react-hot-toast";

export default function PostTypesSettings({ isDark, activeFilters, onDataLoaded }) {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  
  const [newTypeName, setNewTypeName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);

  const hasFetched = useRef(false);

  // --- FONT & COLOR CONFIGURATION ---
  const secondaryTextBase = isDark ? 'text-slate-300' : 'text-slate-600';
  const labelStyle = `${secondaryTextBase} font-bold  tracking-wider`;
  const metaStyle = `${secondaryTextBase} font-bold  tracking-[0.2em]`;


  const toastStyle = {
  style: {
    borderRadius: '12px',
    background: isDark ? '#020617' : '#ffffff',
    color: isDark ? '#cbd5e1' : '#475569', // Slate 300 for dark, 600 for light
    border: isDark ? '1px solid #334155' : '1px solid #e2e8f0',
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    fontWeight: '800',
    padding: '12px 20px',
  },
};

  // --- Icon Logic ---
  const getPostTypeIcon = (typeName, size = 20) => {
    const name = typeName?.toLowerCase() || "";
    if (name.includes("video") || name.includes("reel")) return <Video size={size} />;
    if (name.includes("image") || name.includes("photo") || name.includes("post")) return <ImageIcon size={size} />;
    if (name.includes("story") || name.includes("social")) return <Smartphone size={size} />;
    if (name.includes("carousel")) return <Layers size={size} />;
    if (name.includes("text") || name.includes("blog") || name.includes("caption")) return <FileText size={size} />;
    return <PlayCircle size={size} />; 
  };

  // --- API Handlers ---
  const fetchPostTypes = async () => {
    setLoading(true);
    try {
      const res = await postTypeService.getAdminTypes(); 
      const data = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
      
      // Initial Sort: Active first, then Name
      const sorted = [...data].sort((a, b) => {
        if (a.is_active !== b.is_active) return b.is_active - a.is_active;
        return a.type_name.localeCompare(b.type_name);
      });

      setTypes(sorted);
      if (onDataLoaded) onDataLoaded(sorted);
    } catch (err) {
      console.error("Failed to fetch post formats:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasFetched.current) {
      fetchPostTypes();
      hasFetched.current = true;
    }
  }, []);

const handleToggleStatus = async (type) => {
  const newStatus = type.is_active === 1 ? 0 : 1;
  const statusText = newStatus === 1 ? 'ACTIVATED' : 'HIDDEN';

  // Perform the API call
  const updatePromise = postTypeService.updateType(type.id, { 
    type_name: type.type_name, 
    is_active: newStatus 
  });

  // Use toast.promise so it auto-resolves and disappears
  toast.promise(updatePromise, {
    loading: `${type.type_name.toUpperCase()}: UPDATING...`,
    success: () => {
      setTypes(prev => prev.map(t => t.id === type.id ? { ...t, is_active: newStatus } : t));
      return `${type.type_name.toUpperCase()} ${statusText}`;
    },
    error: "STATUS UPDATE FAILED",
  }, toastStyle); // Pass your slate 300/600 style here
};
  const handleCreateType = async () => {
    if (!newTypeName.trim()) return toast.error("NAME IS REQUIRED");
    setSaving(true);
    const tid = toast.loading("CREATING NEW FORMAT...");
    try {
      await postTypeService.createType(newTypeName);
      toast.success("FORMAT ADDED SUCCESSFULLY", { id: tid });
      setNewTypeName("");
      setIsAdding(false);
      fetchPostTypes();
    } catch (err) {
      toast.error("FAILED TO CREATE FORMAT", { id: tid });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async (type) => {
    if (!editValue.trim()) return toast.error("NAME CANNOT BE EMPTY");
    const tid = toast.loading("SAVING CHANGES...");
    try {
      await postTypeService.updateType(type.id, {
        type_name: editValue,
        is_active: type.is_active
      });
      setTypes(prev => prev.map(t => t.id === type.id ? { ...t, type_name: editValue } : t));
      setEditingId(null);
      toast.success("UPDATED SUCCESSFULLY", { id: tid });
    } catch (err) {
      toast.error("UPDATE FAILED", { id: tid });
    }
  };

  const handlePurgeType = async (type) => {
    const stats = await postTypeService.getTypeStats(type.id);
    const taskCount = stats?.task_count || 0;

    toast((t) => (
      <div className="flex flex-col gap-3">
        <div className="space-y-1">
          <p className="font-bold text-[10px] tracking-widest text-orange-600 uppercase">Confirm Format Purge</p>
          <p className="text-[11px] text-slate-500 leading-normal normal-case">
            Warning: This will also delete <span className="text-orange-600 font-bold">{taskCount} tasks</span> using this format.
          </p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={async () => {
              toast.dismiss(t.id);
              const tid = toast.loading("PURGING FORMAT...", toastStyle);
              try {
                await postTypeService.purgeType(type.id);
                setTypes(prev => prev.filter(item => item.id !== type.id));
                toast.success(`PURGED ${type.type_name.toUpperCase()} & ${taskCount} TASKS`, { ...toastStyle, id: tid });
              } catch (err) {
                toast.error("PURGE FAILED", { ...toastStyle, id: tid });
              }
            }} 
            className="bg-orange-600 text-white px-3 py-1.5 rounded-lg text-[10px] font-black uppercase shadow-lg active:scale-95"
          >
            Confirm Wipeout
          </button>
          <button onClick={() => toast.dismiss(t.id)} className="bg-slate-700 text-white px-3 py-1.5 rounded-lg text-[10px] font-black uppercase opacity-70 hover:opacity-100">
            Cancel
          </button>
        </div>
      </div>
    ), { ...toastStyle, duration: 3500, icon: <Trash2 size={24} className="text-orange-500 shrink-0" /> });
  };

  const startEditing = (type) => {
    setEditingId(type.id);
    setEditValue(type.type_name);
  };

  

 // This memo ONLY handles the search/filter. 
  // It NEVER sorts. Sorting is only done once in fetchPostTypes.
  const filteredAndSorted = useMemo(() => {
    let result = [...types];
    
    const searchTerm = (activeFilters?.global_search?.[0] || "").toLowerCase();
    if (searchTerm) {
      result = result.filter(t => t.type_name.toLowerCase().includes(searchTerm));
    }
    
    const statusFilters = activeFilters?.is_active || [];
    if (statusFilters.length > 0) {
      result = result.filter(t => {
        const statusString = t.is_active === 1 ? "Active" : "Inactive";
        return statusFilters.includes(statusString);
      });
    }

    return result; 
  }, [types, activeFilters?.global_search, activeFilters?.is_active]);

  const secondaryTextColor = isDark ? 'text-slate-300' : 'text-slate-600';

  return (
    <div className={`p-1 sm:p-6 space-y-6 min-h-screen transition-colors duration-300 ${isDark ? 'bg-slate-800/0' : 'bg-slate-100/0'}`}>

{/* HEADER CONTAINER - Standardized for Post Formats */}
<div className="relative flex flex-col items-center justify-center min-h-[120px] sm:min-h-[100px] sm:-mt-8 max-w-7xl mx-auto w-full px-4">
  <div className="text-center">
    {/* 1. MAIN HEADER: Action in Orange, Entity in Blue */}
    <h2 className={`text-2xl font-brand-heading font-bold uppercase tracking-tighter leading-tight ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
      <span className="text-orange-600">Post</span> <span className={isDark ? 'text-[#5A9AFF]' : 'text-[#2D4D94]'}> Formats</span>
    </h2>
    
    {/* 2. SUBHEADER: Identical dividers and layout */}
    <div className="flex items-center justify-center gap-3 mt-0">
      <div className={`h-px w-4 ${isDark ? 'bg-slate-400' : 'bg-slate-400'}`} />
      <p className={`text-[13px] uppercase tracking-[0.1em] ${secondaryTextColor}`}>
        Add New Content Types
      </p>
      <div className={`h-px w-4 ${isDark ? 'bg-slate-400' : 'bg-slate-400'}`} />
    </div>
  </div>
  
  {/* Button Wrapper: Absolute Right on Desktop */}
  <div className="mt-4 sm:mt-0 sm:absolute sm:right-4 sm:top-1/2 sm:-translate-y-1/2 flex items-center">
<Button 
  isDark={isDark} 
  onClick={() => setIsAdding(!isAdding)}
  className={`
    group flex items-center px-5 py-2.5 rounded-xl font-bold uppercase tracking-wider text-xs transition-all border shadow-sm hover:shadow-md active:scale-95 whitespace-nowrap
    ${isDark 
      ? 'bg-slate-900/50 border-slate-700 hover:bg-slate-800' 
      : 'bg-white border-slate-200 hover:bg-slate-50'}
  `}
>
  {/* Conditional Icon with Orange Accent and Animation */}
  {isAdding ? (
    <X 
      size={18} 
      strokeWidth={3} 
      className="text-orange-600 transition-transform duration-300 group-hover:scale-125" 
    />
  ) : (
    <Plus 
      size={18} 
      strokeWidth={3} 
      className="text-orange-600 transition-transform duration-500 ease-out group-hover:rotate-90" 
    />
  )}

  {/* Consistent Blue Text */}
  <span className={`ml-2 ${isDark ? 'text-[#5A9AFF]' : 'text-[#2D4D94]'}`}>
    {isAdding ? "Cancel" : "New Format"}
  </span>
</Button>
  </div>
</div>

      {/* Inline Add Form */}
      {isAdding && (
        <div className={`max-w-7xl mx-auto w-full p-6 rounded-2xl border mb-6 animate-in slide-in-from-top-4 duration-300 ${
          isDark ? 'bg-slate-900 border-indigo-500/30' : 'bg-white border-indigo-200'
        }`}>
          <div className="flex flex-col sm:flex-row items-end gap-4">
            <div className="w-full flex-1 space-y-2">
              <label className={`block ml-1 text-[11px] ${labelStyle}`}>
                Format Name
              </label>
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center border ${isDark ? 'bg-slate-950 border-slate-800' : 'bg-slate-50 border-slate-100'}`}>
                  {getPostTypeIcon(newTypeName)}
                </div>
                <InputField 
                  isDark={isDark} 
                  placeholder="e.g. Instagram Reel, Blog Post" 
                  value={newTypeName}
                  onChange={(e) => setNewTypeName(e.target.value)}
                  autoFocus
                />
              </div>
            </div>
<Button 
  isDark={isDark} 
  onClick={handleCreateType} 
  disabled={saving} 
  className={`
    group flex items-center justify-center px-6 py-2.5 rounded-xl font-bold uppercase tracking-wider text-xs transition-all border shadow-sm hover:shadow-md active:scale-95 whitespace-nowrap w-full sm:w-auto
    ${isDark 
      ? 'bg-slate-900/50 border-slate-700 hover:bg-slate-800 disabled:opacity-50' 
      : 'bg-white border-slate-200 hover:bg-slate-50 disabled:opacity-50'}
  `}
>
  {/* Conditional Icon: Loader or Animated Save */}
  {saving ? (
    <Loader2 className="animate-spin text-slate-500" size={18} />
  ) : (
    <Save 
      size={18} 
      strokeWidth={2} 
      className="text-orange-600 transition-transform duration-300  group-hover:scale-110" 
    />
  )}

  {/* Blue Text */}
  <span className={`ml-2 ${isDark ? 'text-[#5A9AFF]' : 'text-[#2D4D94]'}`}>
    {saving ? "Saving..." : "Save Format"}
  </span>
</Button>
          </div>
        </div>
      )}

      {/* Table Container */}
      <div className={`rounded-2xl border shadow-sm overflow-hidden max-w-7xl mx-auto w-full transition-all ${
        isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'
      }`}>
        <div className="overflow-x-auto w-full">
          {loading && types.length === 0 ? (
            <div className="py-20 flex flex-col items-center justify-center gap-4">
              <Loader2 className="animate-spin text-indigo-500" size={32} />
              <span className={`text-[10px] ${metaStyle}`}>Loading Formats...</span>
            </div>
          ) : (
            <Table className="min-w-[800px] w-full text-left">
              <TableHeader className={isDark ? 'bg-slate-950/50' : 'bg-slate-50/50'}>
                <TableRow className={isDark ? 'border-slate-800' : 'border-slate-100'}>
                  <TableCell isHeader className={`px-6 py-4 text-[11px] font-brand-heading ${labelStyle}`}>Format Name</TableCell>
                  <TableCell isHeader className={`px-6 py-4 text-[11px] font-brand-heading ${labelStyle}`}>Status Toggle</TableCell>
                  <TableCell isHeader className="px-6 py-4 text-right"></TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAndSorted.length > 0 ? (
                  filteredAndSorted.map((type) => (
                    <TableRow 
                      key={type.id} 
                      className={`transition-all group border-b last:border-none ${
                        isDark ? 'hover:bg-slate-800/50 border-slate-800' : 'hover:bg-indigo-50/30 border-slate-50'
                      }`}
                    >
                      <TableCell className="px-6 py-5">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center border transition-colors ${
                            isDark ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'
                          }`}>
                            <span className={type.is_active === 1 ? "text-[#2d4e94]" : "text-orange-300"}>
                              {getPostTypeIcon(editingId === type.id ? editValue : type.type_name)}
                            </span>
                          </div>
                          {editingId === type.id ? (
                            <div className="flex items-center gap-2 flex-1 max-w-xs">
                              <input 
                                className={`w-full bg-transparent border-b-2 border-indigo-500 outline-none font-brand-body-bold text-sm py-1 ${isDark ? 'text-white' : 'text-slate-900'}`}
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                autoFocus
                              />
                            </div>
                          ) : (
                            <div className={`tracking-tight text-base font-brand-body-bold ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
                              {type.type_name}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="px-6 py-5">
                        <div className="flex items-center gap-4">
                          <span className={`text-[11px] font-bold tracking-wider w-16 ${type.is_active === 1 ? 'text-[#2d4e94]' : 'text-slate-400'}`}>
                            {type.is_active === 1 ? 'Active' : 'Hidden'}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleToggleStatus(type)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 focus:outline-none shadow-inner cursor-pointer hover:ring-2 ring-indigo-500/20 ${
                              type.is_active === 1 ? 'bg-[#2d4e94c5]' : 'bg-orange-200'
                            }`}
                          >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-300 ${type.is_active === 1 ? 'translate-x-6' : 'translate-x-1'}`} />
                          </button>
                        </div>
                      </TableCell>
                      <TableCell className="px-6 py-5 text-right">
                        <div className="flex justify-end gap-2">
                          {editingId === type.id ? (
                            <button onClick={() => handleSaveEdit(type)} className="p-2 bg-[#2d4e94c5] text-white rounded-lg hover:bg-indigo-600 transition-all">
                              <Check size={16} />
                            </button>
                          ) : (
                            <>
                              <button onClick={() => startEditing(type)} className={`p-2 rounded-lg border transition-all ${isDark ? 'border-slate-700 text-slate-400 hover:text-white hover:bg-slate-700' : 'border-slate-200 text-slate-400 hover:text-indigo-600 hover:bg-slate-50'}`}>
                                <Edit2 size={16} />
                              </button>
                              <button 
                                onClick={() => handlePurgeType(type)} 
                                className={`p-2 rounded-lg border transition-all ${isDark ? 'border-slate-700 text-orange-600 hover:bg-rose-500/10' : 'border-slate-200 text-orange-500 hover:bg-rose-50'}`}
                              >
                                <Trash2 size={16} />
                              </button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} className="py-24 text-center">
                      <div className="flex flex-col items-center gap-2 opacity-30">
                         <PlayCircle size={40} className={isDark ? 'text-slate-500' : 'text-slate-400'} />
                         <p className={`text-[10px] ${metaStyle}`}>No Formats Found</p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
      
      {/* Toast stage for this settings page */}
    </div>
  );
}